import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

@Injectable({
  providedIn: 'root',
})
export class PdfService {

  /**
   * Renders the given HTML element as a multi-page A4 PDF.
   *
   * The element is cloned and styled for print before capture so the live DOM is
   * never modified: `.pdfHide` elements are removed, backgrounds are set to white,
   * `.panel` width is expanded to 100%, and form control borders are stripped.
   * The clone is rendered off-screen via html2canvas, then sliced into A4 pages
   * and assembled by jsPDF.
   *
   * @param element : HTMLElement - The root DOM element to capture.
   * @param download : boolean - When `true`, immediately triggers a browser file download.
   * @returns A `File` object containing the generated PDF.
   */
  async generateFile(element: HTMLElement, download = false): Promise<File> {
    const clone = element.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('.pdfHide').forEach(el => el.remove());
    clone.querySelectorAll<HTMLElement>('.background').forEach(el => el.style.background = 'white');
    if (clone.classList.contains('background')) clone.style.background = 'white';
    clone.querySelectorAll<HTMLElement>('.footer').forEach(el => el.style.background = 'white');
    clone.querySelectorAll<HTMLElement>('.panel').forEach(el => {
      el.style.width = '100%';
      el.style.padding = '50px';
    });
    clone.querySelectorAll<HTMLElement>('.form-control').forEach(el => {
      el.style.setProperty('border', 'none', 'important');
      el.style.setProperty('background', '#ffffff', 'important');
    });
    clone.querySelectorAll<HTMLElement>('td, th, mat-cell, mat-header-cell, mat-row, mat-header-row').forEach(el => {
      el.style.pageBreakInside = 'avoid';
    });
    // Use the panel's width so the content isn't squished when the outer element is full-screen wide
    const panelEl = element.querySelector<HTMLElement>('.panel');
    const captureWidth = panelEl ? panelEl.offsetWidth : element.offsetWidth;
    clone.style.cssText = `position:absolute;top:-9999px;left:-9999px;width:${captureWidth}px;`;
    const parent = element.parentElement ?? document.body;
    parent.appendChild(clone);
    const canvas = await html2canvas(clone, { scale: 1.5, useCORS: false, allowTaint: false });
    parent.removeChild(clone);
    const imgData = canvas.toDataURL('image/jpeg', 0.7);

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: 'a4', compress: true });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let yOffset = 0;
    while (yOffset < imgHeight) {
      if (yOffset > 0) pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, -yOffset, imgWidth, imgHeight);
      yOffset += pageHeight;
    }

    const blob = pdf.output('blob');
    const file = new File([blob], `loa_submission.pdf`, { type: 'application/pdf' });

    if (download) {
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    }

    return file;
  }
}
